import type {
  AnalyticsConfig,
  AnalyticsEvent,
  SessionInitResponse,
  EventTrackResponse,
  HeartbeatResponse,
  SessionMetadata,
  RateLimitInfo,
} from '../types';
import {
  collectFingerprint,
  detectDevice,
  getScreenInfo,
  parseUtmParams,
  classifyReferrer,
  getPageLoadTime,
  shouldRespectDoNotTrack,
} from '../utils/device';

export class AnalyticsClient {
  private config: Required<AnalyticsConfig>;
  private sessionMetadata: SessionMetadata;
  private eventQueue: AnalyticsEvent[] = [];
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private flushInterval: NodeJS.Timeout | null = null;
  private lastFlushTime: number = Date.now();
  private isInitialized = false;
  private rateLimitInfo: RateLimitInfo | null = null;
  private lastActivityTime: number = Date.now();
  private readonly STORAGE_KEY = 'algino_session';

  constructor(config: AnalyticsConfig) {
    this.config = {
      apiUrl: 'https://analytics.example.com/api/v1',
      debug: false,
      autoTrack: true,
      respectDoNotTrack: true,
      flushInterval: 15000,
      maxBatchSize: 50,
      maxRetries: 3,
      ...config,
    };

    this.sessionMetadata = {
      sessionId: null,
      sessionStartTime: null,
      pageSequence: [],
      pageViewCount: 0,
      customEventCount: 0,
    };
  }

  async init(): Promise<boolean> {
    if (typeof window === 'undefined') {
      this.log('Cannot initialize in server environment');
      return false;
    }

    if (this.isInitialized) {
      this.log('Analytics already initialized');
      return true;
    }

    if (this.config.respectDoNotTrack && shouldRespectDoNotTrack()) {
      this.log('Do Not Track is enabled, skipping initialization');
      return false;
    }

    const existingSession = this.loadSessionFromStorage();
    this.log('Loaded session from storage:', existingSession?.sessionId);

    if (existingSession && this.isSessionValid(existingSession)) {
      this.sessionMetadata = existingSession;
      this.isInitialized = true;

      this.log('Resumed existing session:', this.sessionMetadata.sessionId);

      if (this.config.autoTrack) {
        this.trackPageview();
      }

      this.startHeartbeat();
      this.startFlushInterval();
      this.setupListeners();

      return true;
    }

    try {
      const fingerprint = collectFingerprint();
      const device = detectDevice();
      const screen = getScreenInfo();
      const utmParams = parseUtmParams();
      const referrer = document.referrer || null;
      const sessionStartTime = Date.now();

      const response = await this.request<SessionInitResponse>('/sessions/init', {
        projectId: this.config.projectId,
        pageUrl: window.location.href,
        pageTitle: document.title,
        timestamp: sessionStartTime,
        fingerprint,
        device,
        screen,
        referrer,
        referrerType: classifyReferrer(referrer || ''),
        utmParams,
      });

      if (response?.ok && response.sessionId) {
        this.sessionMetadata.sessionId = response.sessionId;
        this.sessionMetadata.sessionStartTime = sessionStartTime;
        this.isInitialized = true;

        this.log('Session initialized:', response.sessionId);

        // CRITICAL: Save immediately, don't wait for throttle
        this.saveSessionToStorageNow();

        if (this.config.autoTrack) {
          this.trackPageview();
        }

        this.startHeartbeat();
        this.startFlushInterval();
        this.setupListeners();

        return true;
      }

      this.log('Failed to initialize session:', response);
      return false;
    } catch (error) {
      this.log('Initialization error:', error);
      return false;
    }
  }

  trackPageview(url?: string, title?: string): void {
    if (!this.isInitialized || typeof window === 'undefined') return;

    const pageUrl = url || window.location.href;
    const pageTitle = title || document.title;
    const pageLoadTime = getPageLoadTime();
    const referrerUrl =
      this.sessionMetadata.pageSequence[this.sessionMetadata.pageSequence.length - 1] ||
      document.referrer ||
      undefined;

    this.eventQueue.push({
      type: 'pageview',
      url: pageUrl,
      title: pageTitle,
      referrerUrl,
      pageLoadTime: pageLoadTime || undefined,
      timestamp: Date.now(),
    });

    this.sessionMetadata.pageSequence.push(pageUrl);
    this.sessionMetadata.pageViewCount++;

    this.log('Pageview tracked:', pageUrl);
    this.flushIfNeeded();
    this.updateLastActivityTime();
    this.saveSessionToStorageNow();
  }

  track(eventName: string, eventData?: Record<string, any>): void {
    if (!this.isInitialized) {
      this.log('Cannot track event: SDK not initialized');
      return;
    }

    this.eventQueue.push({
      type: 'custom',
      eventName,
      eventData,
      timestamp: Date.now(),
    });

    this.sessionMetadata.customEventCount++;

    this.log('Custom event tracked:', eventName, eventData);
    this.flushIfNeeded();
    this.updateLastActivityTime();
  }

  private async flushIfNeeded(): Promise<void> {
    const timeSinceLastFlush = Date.now() - this.lastFlushTime;
    const shouldFlushBySize = this.eventQueue.length >= this.config.maxBatchSize;
    const shouldFlushByTime = timeSinceLastFlush >= this.config.flushInterval;

    if (shouldFlushBySize || shouldFlushByTime) {
      await this.flushEvents();
    }
  }

  async flushEvents(): Promise<boolean> {
    if (this.eventQueue.length === 0 || !this.sessionMetadata.sessionId) {
      return true;
    }

    const events = this.eventQueue.splice(0, 100);
    this.lastFlushTime = Date.now();

    const response = await this.request<EventTrackResponse>('/events/track', {
      sessionId: this.sessionMetadata.sessionId,
      projectId: this.config.projectId,
      timestamp: Date.now(),
      events,
    });

    if (response?.ok) {
      this.log(`Flushed ${events.length} events`);
      return true;
    } else {
      this.eventQueue.unshift(...events);
      this.log('Failed to flush events:', response);
      return false;
    }
  }

  private startHeartbeat(): void {
    if (typeof window === 'undefined') return;

    // Send heartbeat every 45 seconds (server terminates after 90 seconds of no pings)
    this.heartbeatInterval = setInterval(() => {
      // Only send heartbeat when page is visible
      if (document.visibilityState === 'visible') {
        this.sendHeartbeat();
      }
    }, 45000); // Changed from 30000 to 45000
  }

  private async sendHeartbeat(): Promise<void> {
    if (!this.sessionMetadata.sessionId) return;

    const response = await this.request<HeartbeatResponse>('/sessions/heartbeat', {
      sessionId: this.sessionMetadata.sessionId,
      projectId: this.config.projectId,
      timestamp: Date.now(),
      currentPageUrl: window.location.href,
      isActive: true,
    });

    if (response?.ok) {
      this.log('Heartbeat sent');
    } else if (!response) {
      // 404 or other error - session may have expired
      this.log('Heartbeat failed - session may have expired');
    }
  }

  private startFlushInterval(): void {
    this.flushInterval = setInterval(() => {
      this.flushEvents();
    }, this.config.flushInterval);
  }

  private setupListeners(): void {
    if (typeof window === 'undefined') return;

    // Track user activity
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    activityEvents.forEach(event => {
      document.addEventListener(event, () => {
        this.updateLastActivityTime();
      }, { passive: true });
    });

    // Visibility change - pause/resume heartbeat
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.log('Tab hidden - saving session');
        this.saveSessionToStorageNow();
      } else if (document.visibilityState === 'visible') {
        this.log('Tab visible');
        if (this.isInitialized) {
          // Send immediate heartbeat when tab becomes visible
          this.sendHeartbeat();
        }
        this.updateLastActivityTime();
      }
    });

    // Page hide - save session for persistence
    window.addEventListener('pagehide', (event: any) => {
      this.log('pagehide event fired, persisted:', event.persisted);
      this.saveSessionToStorageNow();
    });

    // Before unload - save session for page reloads/navigation
    window.addEventListener('beforeunload', () => {
      this.log('beforeunload event fired - saving session');
      this.saveSessionToStorageNow();
    });

    // External link clicks
    document.addEventListener('click', (e) => {
      const link = (e.target as Element)?.closest('a');
      if (link && link instanceof HTMLAnchorElement) {
        try {
          const href = link.getAttribute('href');
          if (href && !href.startsWith('#')) {
            const destinationUrl = new URL(href, window.location.origin);
            if (destinationUrl.hostname !== window.location.hostname) {
              this.log('External link clicked');
            }
          }
        } catch (error) {
          this.log('Link click error:', error);
        }
      }
    }, true);

    // SPA support
    window.addEventListener('popstate', () => {
      this.log('History navigation');
      if (this.config.autoTrack) {
        this.trackPageview();
      }
      this.saveSessionToStorageNow();
    });
  }

  private isPageRefresh(): boolean {
    try {
      const navEntries = window.performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const navEntry = navEntries[0] as PerformanceNavigationTiming;
        return navEntry.type === 'reload';
      }
    } catch (error) {
      this.log('Could not determine if page refresh:', error);
    }

    try {
      if ((window.performance as any).navigation) {
        return (window.performance as any).navigation.type === 1;
      }
    } catch (error) {
      this.log('Fallback navigation check failed:', error);
    }

    return false;
  }

  private async request<T>(
    endpoint: string,
    data: any,
    retries = this.config.maxRetries
  ): Promise<T | null> {
    for (let i = 0; i < retries; i++) {
      try {
        const response = await fetch(`${this.config.apiUrl}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': this.config.apiKey,
          },
          body: JSON.stringify(data),
        });

        this.parseRateLimitHeaders(response);

        const result = await response.json();

        if (this.rateLimitInfo && this.rateLimitInfo.remainingIP < 10) {
          this.log('Warning: Approaching rate limit', this.rateLimitInfo.remainingIP);
        }

        if (!result.ok) {
          // Handle 404 - session expired on server
          if (response.status === 404 && (endpoint.includes('/events') || endpoint.includes('/heartbeat'))) {
            this.log('Session expired (404) - reinitializing...');

            // Clear old session
            if (typeof window !== 'undefined' && window.localStorage) {
              localStorage.removeItem(this.STORAGE_KEY);
            }

            // Stop intervals
            if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
            if (this.flushInterval) clearInterval(this.flushInterval);

            // Reset state
            this.isInitialized = false;
            this.sessionMetadata = {
              sessionId: null,
              sessionStartTime: null,
              pageSequence: [],
              pageViewCount: 0,
              customEventCount: 0,
            };

            // Reinitialize new session
            const initialized = await this.init();

            // Retry the original request with new session
            if (initialized && endpoint.includes('/events')) {
              return this.request<T>(endpoint, { ...data, sessionId: this.sessionMetadata.sessionId }, retries - 1);
            }

            return null;
          }

          if (response.status === 429) {
            const retryAfter = result.retryAfter || Math.pow(2, i);
            this.log(`Rate limited, retrying after ${retryAfter}s`);
            await this.sleep(retryAfter * 1000);
            continue;
          }

          this.log('API error:', result.error, result.errors);
          return null;
        }

        return result as T;
      } catch (error) {
        if (i < retries - 1) {
          await this.sleep(Math.pow(2, i) * 1000);
          continue;
        }
        this.log('Network error:', error);
        return null;
      }
    }
    return null;
  }

  private parseRateLimitHeaders(response: Response): void {
    const limitIP = response.headers.get('X-RateLimit-Limit-IP');
    const remainingIP = response.headers.get('X-RateLimit-Remaining-IP');
    const resetIP = response.headers.get('X-RateLimit-Reset-IP');
    const limitProject = response.headers.get('X-RateLimit-Limit-Project');
    const remainingProject = response.headers.get('X-RateLimit-Remaining-Project');
    const resetProject = response.headers.get('X-RateLimit-Reset-Project');

    if (limitIP && remainingIP && resetIP && limitProject && remainingProject && resetProject) {
      this.rateLimitInfo = {
        limitIP: parseInt(limitIP),
        remainingIP: parseInt(remainingIP),
        resetIP: parseInt(resetIP),
        limitProject: parseInt(limitProject),
        remainingProject: parseInt(remainingProject),
        resetProject: parseInt(resetProject),
      };
    }
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private log(...args: any[]): void {
    if (this.config.debug) {
      console.log('[Analytics SDK]', ...args);
    }
  }

  private updateLastActivityTime(): void {
    this.lastActivityTime = Date.now();
  }

  private saveSessionToStorageNow(): void {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const sessionData = {
          ...this.sessionMetadata,
        };
        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(sessionData));
        this.log('Session saved to localStorage:', this.sessionMetadata.sessionId);
      } catch (error) {
        this.log('Failed to save session to localStorage:', error);
      }
    }
  }

  private loadSessionFromStorage(): SessionMetadata | null {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const sessionData = localStorage.getItem(this.STORAGE_KEY);
        if (sessionData) {
          const parsed = JSON.parse(sessionData) as SessionMetadata;
          this.log('Session found in localStorage:', parsed.sessionId);
          return parsed;
        }
      } catch (error) {
        this.log('Failed to load session from localStorage:', error);
      }
    }
    return null;
  }

  private isSessionValid(session: SessionMetadata): boolean {
    if (!session.sessionId || !session.sessionStartTime) {
      return false;
    }

    // Session is valid if less than 2 minutes old (allows for 90s server timeout + buffer)
    const sessionAge = Date.now() - session.sessionStartTime;
    const isValid = sessionAge < 120000; // 2 minutes

    if (!isValid) {
      this.log('Session expired (age:', sessionAge, 'ms)');
      localStorage.removeItem(this.STORAGE_KEY);
    }

    return isValid;
  }

  getSessionId(): string | null {
    return this.sessionMetadata.sessionId;
  }

  getRateLimitInfo(): RateLimitInfo | null {
    return this.rateLimitInfo;
  }

  // Remove endSession method - sessions end automatically on server
  async destroy(): Promise<void> {
    this.log('Destroying client');
    
    // Stop intervals
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    if (this.flushInterval) clearInterval(this.flushInterval);
    
    // Clear storage
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.removeItem(this.STORAGE_KEY);
    }

    this.isInitialized = false;
    this.sessionMetadata = {
      sessionId: null,
      sessionStartTime: null,
      pageSequence: [],
      pageViewCount: 0,
      customEventCount: 0,
    };
  }
}
